import React from "react";
import { ArrowLeft } from "lucide-react";
import { useLocation, useNavigationType } from "react-router-dom";

import { addAssetWatch, getAssetTrackingOverview, screenerFeedback, screenerNl, screenerRun } from "../../api/client";
import { useOverviewAgent } from "../asset-overview/OverviewAgentPanel";
import { openHoldingPage } from "../asset-overview/openHolding";
import resultStyles from "../single-asset-agent-dev/singleAssetResult.module.css";
import { ScreenerFeedback } from "./ScreenerFeedback";
import { ScreenerResults, Skeleton } from "./ScreenerResults";
import { ScreenerSearch } from "./ScreenerSearch";
import {
  EXAMPLES,
  LOOKUP_EXAMPLES,
  buildRunBody,
  clearLast,
  describeError,
  initialState,
  loadLast,
  reducer,
  saveLast,
  withoutCondition,
} from "./screenerModel";
import styles from "./screenerPage.module.css";

// 添加关注页：用一句话（或名称 / 代码）找 A股 / ETF / 场外基金，每行可查看、可加关注。
// 设计：docs/sdd/2026-09-23-overview-tags-screener/prototype.html ③（v23）；
// 接口：docs/sdd/2026-10-01-nl-screener/spec.md §6。
export function ScreenerPage({ navigate, onBack, onLogout }) {
  const [agentError, setAgentError] = React.useState("");
  const agent = useOverviewAgent({ navigate, onLogout, onError: setAgentError });  // 左侧 Agent 栏，同概览 / 标签页
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const [input, setInput] = React.useState("");
  const [held, setHeld] = React.useState(() => new Map());  // 代码 → holding_id（已持有的才有）
  const [watched, setWatched] = React.useState(() => new Set());
  const [followBusy, setFollowBusy] = React.useState(() => new Set());
  const [notice, setNotice] = React.useState("");
  const navigationType = useNavigationType();
  const seqRef = React.useRef(0);
  const topRef = React.useRef(null);
  const nextSeq = () => { seqRef.current += 1; return seqRef.current; };
  // 最新 state 给异步回调读（reducer 的 state 在闭包里会过期）。
  const stateRef = React.useRef(state);
  stateRef.current = state;

  // 已持有 / 已关注：联想行没有这两个标记，取一次概览自己对；失败只是少了状态，不影响查找。
  React.useEffect(() => {
    let active = true;
    getAssetTrackingOverview()
      .then((overview) => {
        if (!active) return;
        setHeld(new Map((overview?.holdings ?? []).map((row) => [row.asset_code, row.holding_id ?? ""])));
        setWatched(new Set((overview?.watches ?? []).map((row) => row.asset_code)));
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const runRefine = React.useCallback(async ({ understood, sort, page }) => {
    const seq = nextSeq();
    dispatch({ type: "refine", seq, understood, sort, page });
    try {
      const payload = await screenerRun(buildRunBody(understood ?? stateRef.current.understood, { sort: sort ?? stateRef.current.sort ?? undefined, page }));
      dispatch({ type: "run_ok", seq, payload });
    } catch (error) {
      dispatch({ type: "fail", seq, kind: "run", message: describeError(error) });
    }
  }, []);

  // 从资产页点「返回」（state.restore）/ 后退 / 刷新回来（POP）：用上次的条件走 /run 恢复（不调 LLM、不计费）。
  // 从概览点「添加关注」进来（PUSH）是新的一次，从空白开始。
  // restoredRef：开发模式的 StrictMode 会把 effect 跑两遍，只恢复一次（生产里本来就只跑一遍）。
  const restoreRequested = Boolean(useLocation().state?.restore);
  const restoreOnEnter = navigationType === "POP" || restoreRequested;
  const restoredRef = React.useRef(false);
  React.useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    if (!restoreOnEnter) clearLast();
    const saved = restoreOnEnter ? loadLast() : null;
    if (saved) {
      setInput(saved.query);  // 恢复回来输入框里还是上次那句话
      const seq = nextSeq();
      dispatch({ type: "restore", seq, saved });
      screenerRun(buildRunBody(saved.understood, { sort: saved.sort ?? undefined, page: saved.page }))
        .then((payload) => dispatch({ type: "run_ok", seq, payload }))
        .catch(() => { clearLast(); dispatch({ type: "reset", seq: nextSeq() }); });
    }
  }, [restoreOnEnter]);

  React.useEffect(() => { saveLast(state); }, [state]);

  // 左侧 Agent 判断用户在「找一类标的」时转交过来的那句话：填进输入框并查一次。
  // 用完立刻 replace 掉路由态——否则刷新（POP）会把同一句话再查一遍、再扣一次光点。
  // handledRef 同上，防开发模式 StrictMode 把 effect 跑两遍。
  const agentQuery = useLocation().state?.agentQuery;
  const handledAgentQueryRef = React.useRef(null);
  React.useEffect(() => {
    if (!agentQuery || handledAgentQueryRef.current === agentQuery) return;
    handledAgentQueryRef.current = agentQuery;
    navigate("/screener", { replace: true, state: null });
    setInput(agentQuery);
    void submit(agentQuery);
  }, [agentQuery]);  // eslint-disable-line react-hooks/exhaustive-deps

  // 输入框里的话是唯一的事实来源：每次「查找」都是把整句话重新交给自然语言查找，结果后文案保留，想调整就改这句话再查。
  async function submit(query) {
    setNotice("");
    const seq = nextSeq();
    dispatch({ type: "start", seq, query });
    try {
      const payload = await screenerNl(query);
      dispatch({ type: "nl_ok", seq, payload });
    } catch (error) {
      dispatch({ type: "fail", seq, kind: "nl", message: describeError(error) });
    }
  }

  function tryExample(query) {
    setInput(query);
    void submit(query);
  }

  function removeCondition(index) {
    // 去掉一个条件 = 用户在说「这个条件不是我要的」，记下来供复盘；失败不打扰
    if (state.queryId) {
      const label = state.understood?.conditions?.[index]?.label ?? "";
      screenerFeedback({ query_id: state.queryId, verdict: "edit", note: label }).catch(() => {});
    }
    const next = withoutCondition(state.understood, index);
    if (!next) {  // 条件都去光了：回到首页态，而不是把整个市场列出来
      clearLast();
      setInput("");  // 条件都没了，那句话也就不对应任何结果
      dispatch({ type: "reset", seq: nextSeq() });
      return;
    }
    void runRefine({ understood: next, page: 1 });
  }

  async function follow(code) {
    if (followBusy.has(code) || watched.has(code) || held.has(code)) return;
    setNotice("");
    setFollowBusy((current) => new Set(current).add(code));
    try {
      await addAssetWatch(code);
      setWatched((current) => new Set(current).add(code));
    } catch (error) {
      if (error?.payload?.error?.code === "asset_already_held") {
        setHeld((current) => new Map(current).set(code, ""));  // 其实已持有：改成「已持有」
      } else {
        setNotice(error?.message || "暂时无法加入关注，请稍后重试。");
      }
    } finally {
      setFollowBusy((current) => { const next = new Set(current); next.delete(code); return next; });
    }
  }

  // 已持有的标的直接打开持仓核算页（同概览：无论从哪个入口进来都一样），否则打开行情。
  async function openAsset(code) {
    const holdingId = held.get(code);
    if (!holdingId) {  // 没持有（或刚被告知已持有但还没有 holding_id）：行情页，?asset= 是它的直达路径
      navigate(`/single?asset=${encodeURIComponent(code)}`, { state: { returnTo: "/screener" } });  // 行情页「返回」回到这里（带着搜索结果）
      return;
    }
    setNotice("");
    try {
      await openHoldingPage(navigate, { holding_id: holdingId, asset_code: code }, { returnTo: "/screener" });
    } catch (error) {
      setNotice(error?.message || "暂时无法打开该持仓。");
    }
  }

  const loadingFresh = state.phase === "loading" && !state.result;
  // 新一句话在途时旧结果不撤掉（免得闪），但压暗并禁用操作，别让人对着旧表点。
  const shown = { ...state, busy: state.busy || state.phase === "loading", searching: state.phase === "loading" };
  const restoring = state.phase === "result" && !state.result && state.busy;
  const home = state.phase === "idle";
  const searchProps = {
    value: input, onChange: setInput, onSubmit: submit, busy: state.phase === "loading",
    held, watched, followBusy, onFollow: follow, onOpenAsset: openAsset,
  };

  const title = (
    <div className={styles.title}>
      <button className={resultStyles.resultBackButton} type="button" onClick={onBack} aria-label="返回概览" title="返回概览">
        <ArrowLeft size={18} />
      </button>
      <h1>添加关注</h1>
    </div>
  );

  if (home) {
    return (
      <main className={styles.page} id="main-content" style={agent.style}>
        {agent.panel}
        <div className={styles.homeSurface}>
          {agentError && <p className={styles.notice} role="alert">{agentError}</p>}
          {title}
          <div className={styles.homeCenter}>
            <ScreenerSearch variant="home" {...searchProps} />
            {!input.trim() && (
              <div className={styles.tryRow}>
                <span>试试</span>
                {[...LOOKUP_EXAMPLES, ...EXAMPLES].map((query) => (
                  <button key={query} type="button" className={styles.tryChip} onClick={() => tryExample(query)}>{query}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.page} id="main-content" style={agent.style} ref={topRef}>
      {agent.panel}
      <div className={`${resultStyles.resultSurface} ${styles.stack}`}>
        {agentError && <p className={styles.notice} role="alert">{agentError}</p>}
        <header className={styles.head}>
          {title}
          <ScreenerSearch variant="bar" {...searchProps} />
        </header>
        {notice && <p className={styles.notice} role="status">{notice}</p>}
        {(loadingFresh || restoring) && <Skeleton label={restoring ? "正在恢复上次的结果" : "正在理解你的问题"} />}
        {state.phase === "error" && (
          <section className={styles.card}>
            <div className={styles.emptyBox} role="alert">
              <p>{state.error}</p>
              <button type="button" className={styles.retry} onClick={() => { void submit(state.query); }}>重试</button>
            </div>
          </section>
        )}
        <ScreenerResults
          state={shown}
          held={new Set(held.keys())}
          watched={watched}
          followBusy={followBusy}
          onRemoveCondition={removeCondition}
          onSort={(sort) => void runRefine({ sort, page: 1 })}
          onPage={(page) => { topRef.current?.scrollIntoView({ block: "start" }); void runRefine({ page }); }}
          onFollow={follow}
          onOpen={openAsset}
        />
        {state.phase === "result" && state.queryId && !shown.busy && <ScreenerFeedback key={state.queryId} queryId={state.queryId} />}
      </div>
    </main>
  );
}
